import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import CreateCustomerModal from '../components/CreateCustomerModal';
import { apiClient } from '../lib/apiClient';
import TrelloIcon from '../components/icons/TrelloIcon';
import LinearIcon from '../components/icons/LinearIcon';
import JiraIcon from '../components/icons/JiraIcon';
import SlackIcon from '../components/icons/SlackIcon';
import { 
  CustomerProfile, 
  EnhancedCustomersResponse, 
  CustomerStatus,
  CustomerDiscoveryResult 
} from '../types/api';
import { 
  Search, 
  Plus, 
  Filter, 
  Download,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  MessageSquare,
  Mail,
  ExternalLink,
  Activity,
  Calendar,
  X,
  Users,
  CheckCircle
} from 'lucide-react';

const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Input values (what user is typing)
  const [searchTermInput, setSearchTermInput] = useState('');
  const [healthMinInput, setHealthMinInput] = useState<number | undefined>(undefined);
  const [healthMaxInput, setHealthMaxInput] = useState<number | undefined>(undefined);
  
  // Applied filters (what's actually sent to backend)
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CustomerStatus | ''>('');
  const [healthMinFilter, setHealthMinFilter] = useState<number | undefined>(undefined);
  const [healthMaxFilter, setHealthMaxFilter] = useState<number | undefined>(undefined);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState<CustomerDiscoveryResult | null>(null);
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerProfile | null>(null);
  const filtersAppliedRef = useRef(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 20;

  // Apply filters from navigation state on mount
  useEffect(() => {
    if (location.state?.filters && !filtersAppliedRef.current) {
      const filters = location.state.filters as {
        status?: CustomerStatus;
        health_min?: number;
        health_max?: number;
      };
      
      if (filters.status) {
        setStatusFilter(filters.status);
      }
      if (filters.health_min !== undefined) {
        setHealthMinInput(filters.health_min);
        setHealthMinFilter(filters.health_min);
      }
      if (filters.health_max !== undefined) {
        setHealthMaxInput(filters.health_max);
        setHealthMaxFilter(filters.health_max);
      }
      
      filtersAppliedRef.current = true;
      
      // Clear location state to prevent re-applying on re-renders
      window.history.replaceState({ ...window.history.state, state: null }, '');
    }
  }, [location.state]);

  const fetchCustomers = useCallback(async (page = 0, search = '', status = '', healthMin?: number, healthMax?: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        limit: pageSize,
        offset: page * pageSize,
      };
      
      if (search) params.search = search;
      if (status) params.status = status;
      if (healthMin !== undefined) params.health_min = healthMin;
      if (healthMax !== undefined) params.health_max = healthMax;
      
      const response = await apiClient.getCustomers(params);
      
      setCustomers(response.customers);
      setTotalCount(response.pagination.total);
      setHasMore(response.pagination.has_more);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  // Only trigger fetch when applied filters change, NOT on input change
  useEffect(() => {
    fetchCustomers(currentPage, searchTerm, statusFilter, healthMinFilter, healthMaxFilter);
  }, [fetchCustomers, currentPage, searchTerm, statusFilter, healthMinFilter, healthMaxFilter]);

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
    e.preventDefault();
    }
    // Apply the input values as filters - this will trigger the useEffect
    setSearchTerm(searchTermInput);
    setHealthMinFilter(healthMinInput);
    setHealthMaxFilter(healthMaxInput);
    setCurrentPage(0);
  };

  const handleStatusFilter = (status: CustomerStatus | '') => {
    // Status filter still applies immediately for better UX
    setStatusFilter(status);
    setCurrentPage(0);
  };

  const handleHealthInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const clearFilters = () => {
    // Clear both input values and applied filters
    setSearchTermInput('');
    setHealthMinInput(undefined);
    setHealthMaxInput(undefined);
    setStatusFilter('');
    setHealthMinFilter(undefined);
    setHealthMaxFilter(undefined);
    setSearchTerm('');
    setCurrentPage(0);
  };

  const hasActiveFilters = () => {
    return statusFilter !== '' || healthMinFilter !== undefined || healthMaxFilter !== undefined || searchTerm !== '';
  };

  const handleDiscoverCustomers = async () => {
    try {
      setDiscoveryLoading(true);
      const results = await apiClient.discoverSlackCustomers();
      setDiscoveryResults(results as CustomerDiscoveryResult);
      
      // Refresh customer list to show any newly created customers
      fetchCustomers(currentPage, searchTerm, statusFilter, healthMinFilter, healthMaxFilter);
      
      // Invalidate unmapped sources query to refresh Settings page
      // This will trigger a refetch when user navigates to Settings
      queryClient.invalidateQueries({ queryKey: ['unmappedSources'] });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discover customers');
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleSourcesClick = (customer: CustomerProfile) => {
    setSelectedCustomer(customer);
    setShowSourcesModal(true);
  };

  const getHealthStatusVariant = (status: CustomerStatus): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'active':
        return 'default';
      case 'prospect':
        return 'secondary';
      case 'churned':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getHealthScoreColor = (score?: number) => {
    if (!score) return 'text-gray-400';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthScoreIcon = (score?: number) => {
    if (!score) return <Minus className="w-4 h-4" />;
    if (score >= 80) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (score >= 60) return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    return <TrendingDown className="w-4 h-4 text-red-600" />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Accounts</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Manage your account relationships and track their health scores.
          </p>
        </div>
        
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
          <Button
            variant="outline"
            onClick={handleDiscoverCustomers}
            disabled={discoveryLoading}
            className="w-full sm:w-auto"
          >
            {discoveryLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Discover Accounts
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate('/settings')}
            className="w-full sm:w-auto"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Map Sources
          </Button>
          
          <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Add Account
          </Button>
        </div>
      </div>

      {/* Discovery Results */}
      {discoveryResults && discoveryResults.summary && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <CardHeader>
            <CardTitle className="text-blue-800 dark:text-blue-200">Discovery Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="font-medium text-blue-700 dark:text-blue-300">Channels Scanned</div>
                <div className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {discoveryResults.channels_scanned}
                </div>
              </div>
              <div>
                <div className="font-medium text-blue-700 dark:text-blue-300">Discovered</div>
                <div className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {discoveryResults.summary.total_discovered}
                </div>
              </div>
              <div>
                <div className="font-medium text-blue-700 dark:text-blue-300">High Confidence</div>
                <div className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {discoveryResults.summary.high_confidence}
                </div>
              </div>
              <div>
                <div className="font-medium text-blue-700 dark:text-blue-300">Auto-Mapped</div>
                <div className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {discoveryResults.summary.auto_mapped}
                </div>
              </div>
            </div>
            
            {discoveryResults.summary.needs_manual_mapping > 0 && (
              <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-md">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {discoveryResults.summary.needs_manual_mapping} sources need manual mapping. 
                  <Link 
                    to="/settings" 
                    className="ml-2 text-yellow-900 dark:text-yellow-100 underline hover:no-underline"
                  >
                    Map them now →
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Active Filters:</span>
              {statusFilter && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Status: {statusFilter}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setStatusFilter('')} />
                </Badge>
              )}
              {healthMinFilter !== undefined && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Health Min: {healthMinFilter}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => {
                    setHealthMinFilter(undefined);
                    setHealthMinInput(undefined);
                  }} />
                </Badge>
              )}
              {healthMaxFilter !== undefined && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Health Max: {healthMaxFilter}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => {
                    setHealthMaxFilter(undefined);
                    setHealthMaxInput(undefined);
                  }} />
                </Badge>
              )}
              {searchTerm && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Search: {searchTerm}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => {
                    setSearchTerm('');
                    setSearchTermInput('');
                  }} />
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="ml-auto text-xs"
              >
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4 sm:items-end">
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-medium text-muted-foreground mb-2">
                Search Accounts
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  id="search"
                  type="text"
                  value={searchTermInput}
                  onChange={(e) => setSearchTermInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  placeholder="Search by name or domain..."
                  className="pl-10 pr-4 py-2 w-full border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                />
              </div>
            </div>
            
            <div className="sm:min-w-0 sm:w-auto">
              <label htmlFor="status" className="block text-sm font-medium text-muted-foreground mb-2">
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value as CustomerStatus | '')}
                className="w-full sm:w-auto px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="prospect">Prospect</option>
                <option value="churned">Churned</option>
              </select>
            </div>
            
            <div className="sm:min-w-0 sm:w-auto">
              <label htmlFor="healthMin" className="block text-sm font-medium text-muted-foreground mb-2">
                Min Health
              </label>
              <input
                id="healthMin"
                type="number"
                min="0"
                max="100"
                value={healthMinInput ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setHealthMinInput(value ? parseInt(value) : undefined);
                }}
                onKeyPress={handleHealthInputKeyPress}
                placeholder="Min"
                className="w-full sm:w-24 px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
              />
            </div>

            <div className="sm:min-w-0 sm:w-auto">
              <label htmlFor="healthMax" className="block text-sm font-medium text-muted-foreground mb-2">
                Max Health
              </label>
              <input
                id="healthMax"
                type="number"
                min="0"
                max="100"
                value={healthMaxInput ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setHealthMaxInput(value ? parseInt(value) : undefined);
                }}
                onKeyPress={handleHealthInputKeyPress}
                placeholder="Max"
                className="w-full sm:w-24 px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Filtering...
                </>
              ) : (
                <>
              <Filter className="w-4 h-4 mr-2" />
              Filter
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="pt-6">
            <div className="flex items-center text-destructive">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Account Overview ({totalCount} total)
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {customers.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-muted-foreground text-lg mb-2">No accounts found</div>
              <p className="text-muted-foreground mb-4">
                {searchTerm || statusFilter 
                  ? "Try adjusting your search criteria"
                  : "Get started by adding your first account or discovering them from integrations"
                }
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
            </div>
          ) : (
            <>
              {/* Desktop Table - Hidden on mobile */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Health Score</TableHead>
                      <TableHead>MRR</TableHead>
                      <TableHead>Sources</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.customer_id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{customer.name}</div>
                            {customer.domains && customer.domains.length > 0 && (
                              <div className="text-sm text-muted-foreground">
                                {customer.domains.slice(0, 2).join(', ')}
                                {customer.domains.length > 2 && ` +${customer.domains.length - 2} more`}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getHealthStatusVariant(customer.status)}>
                            {customer.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getHealthScoreIcon(customer.health_score)}
                            <span className={getHealthScoreColor(customer.health_score)}>
                              {customer.health_score ? `${customer.health_score}/100` : 'N/A'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(customer.mrr)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className="cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            onClick={() => handleSourcesClick(customer)}
                          >
                            {customer.sources_count} sources
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(customer.recent_activity)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/customers/${customer.customer_id}`)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards - Shown only on mobile */}
              <div className="md:hidden space-y-4">
                {customers.map((customer) => (
                  <Card key={customer.customer_id} className="p-4">
                    <div className="space-y-3">
                      {/* Customer Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-base truncate">{customer.name}</h3>
                          {customer.domains && customer.domains.length > 0 && (
                            <p className="text-sm text-muted-foreground truncate">
                              {customer.domains.slice(0, 2).join(', ')}
                              {customer.domains.length > 2 && ` +${customer.domains.length - 2} more`}
                            </p>
                          )}
                        </div>
                        <Badge variant={getHealthStatusVariant(customer.status)} className="ml-2">
                          {customer.status}
                        </Badge>
                      </div>

                      {/* Metrics Row */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Health Score</div>
                          <div className="flex items-center gap-1">
                            {getHealthScoreIcon(customer.health_score)}
                            <span className={`font-medium ${getHealthScoreColor(customer.health_score)}`}>
                              {customer.health_score ? `${customer.health_score}/100` : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">MRR</div>
                          <div className="font-medium">{formatCurrency(customer.mrr)}</div>
                        </div>
                      </div>

                      {/* Sources and Activity */}
                      <div className="flex items-center justify-between text-sm">
                        <div>
                          <span className="text-muted-foreground">Sources: </span>
                          <span className="font-medium">{customer.sources_count}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Activity: </span>
                          <span>{formatDate(customer.recent_activity)}</span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSourcesClick(customer)}
                          className="flex-1"
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Sources
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/customers/${customer.customer_id}`)}
                          className="flex-1"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {(hasMore || currentPage > 0) && (
                <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mt-6">
                  <div className="text-sm text-muted-foreground text-center sm:text-left">
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount} accounts
                  </div>
                  
                  <div className="flex gap-2 justify-center sm:justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className="flex-1 sm:flex-initial"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!hasMore}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="flex-1 sm:flex-initial"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Customer Modal */}
      {showCreateModal && (
        <CreateCustomerModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchCustomers(currentPage, searchTerm, statusFilter, healthMinFilter, healthMaxFilter);
          }}
        />
      )}

      {/* Sources Modal */}
      {showSourcesModal && selectedCustomer && (
        <SourcesModal
          customerId={selectedCustomer.customer_id}
          customerName={selectedCustomer.name}
          onClose={() => {
            setShowSourcesModal(false);
            setSelectedCustomer(null);
          }}
          onSourceChange={() => {
            // Refresh the main customer list when sources change
            fetchCustomers(currentPage, searchTerm, statusFilter, healthMinFilter, healthMaxFilter);
          }}
        />
      )}
    </div>
  );
};

// Sources Modal Component (reused from CustomerDetailPage)
interface SourcesModalProps {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onSourceChange?: () => void;
}

const SourcesModal: React.FC<SourcesModalProps> = ({ customerId, customerName, onClose, onSourceChange }) => {
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingSource, setRemovingSource] = useState<string | null>(null);

  const fetchSources = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getCustomerSources(customerId) as { sources: any[] };
      setSources(response.sources || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  const handleRemoveSource = async (source: any) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove "${source.source_name}" from this customer? This will make it an unmapped source.`
    );
    
    if (!confirmed) return;

    try {
      setRemovingSource(source.source_id);
      await apiClient.unmapSourceFromCustomer(source.source_type, source.source_id);
      
      // Refresh sources list
      await fetchSources();
      
      // Notify parent component to refresh customer list
      onSourceChange?.();
    } catch (err) {
      console.error('Failed to remove source:', err);
      alert('Failed to remove source. Please try again.');
    } finally {
      setRemovingSource(null);
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType.toLowerCase()) {
      case 'slack_channel':
        return (
          <div className="w-4 h-4 rounded bg-purple-100 flex items-center justify-center">
            <SlackIcon size={12} />
          </div>
        );
      case 'email_domain':
        return <Mail className="w-4 h-4 text-blue-600" />;
      case 'crm_account':
        return <ExternalLink className="w-4 h-4 text-green-600" />;
      case 'jira_org':
        return (
          <div className="w-4 h-4 rounded bg-blue-100 flex items-center justify-center">
            <JiraIcon className="w-3 h-3" />
          </div>
        );
      case 'zendesk_org':
        return <div className="w-4 h-4 rounded bg-green-100 flex items-center justify-center">
          <span className="text-xs font-bold text-green-600">Z</span>
        </div>;
      case 'trello_org':
        return (
          <div className="w-4 h-4 rounded bg-blue-100 flex items-center justify-center">
            <TrelloIcon className="w-3 h-3" />
          </div>
        );
      case 'linear_org':
        return (
          <div className="w-4 h-4 rounded bg-indigo-100 flex items-center justify-center">
            <LinearIcon className="w-3 h-3" />
          </div>
        );
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSourceTypeLabel = (sourceType: string) => {
    // Use the API client helper function
    return (apiClient as any).getSourceTypeLabel(sourceType);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const extractMetadataInfo = (metadata: any) => {
    if (!metadata || typeof metadata !== 'object') return null;
    
    const info: any = {};
    
    // Extract interesting fields from metadata
    if (metadata.channel_name) info.channelName = metadata.channel_name;
    if (metadata.member_count !== undefined) info.memberCount = metadata.member_count;
    if (metadata.has_external_users !== undefined) info.hasExternalUsers = metadata.has_external_users;
    if (metadata.is_customer_channel !== undefined) info.isCustomerChannel = metadata.is_customer_channel;
    if (metadata.reasoning && Array.isArray(metadata.reasoning)) info.reasoning = metadata.reasoning;
    
    return Object.keys(info).length > 0 ? info : null;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border-0 bg-card/95 backdrop-blur-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6 border-b border-border">
          <div>
            <CardTitle className="text-2xl font-bold text-foreground">Data Sources</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Connected sources for account {customerName}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="hover:bg-muted rounded-full">
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No data sources connected to this customer</p>
              <p className="text-sm text-muted-foreground mb-6">
                Connect sources through the Settings page to start tracking customer activity
              </p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4">
                {sources.map((source, index) => {
                  const metadataInfo = extractMetadataInfo(source.metadata);
                  
                  return (
                    <div key={index} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {getSourceIcon(source.source_type)}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-semibold text-foreground">{source.source_name}</h4>
                              <Badge variant="outline" className="text-xs">
                                {getSourceTypeLabel(source.source_type)}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                              <div>
                                <span className="font-medium">Source ID:</span>
                                <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                                  {source.source_id}
                                </p>
                              </div>
                              
                              {source.confidence_score && (
                                <div>
                                  <span className="font-medium">Confidence Score:</span>
                                  <p className="mt-1">
                                    <Badge 
                                      variant={source.confidence_score >= 80 ? "default" : source.confidence_score >= 60 ? "secondary" : "destructive"}
                                      className="text-xs"
                                    >
                                      {source.confidence_score}%
                                    </Badge>
                                  </p>
                                </div>
                              )}
                              
                              {source.last_scanned && (
                                <div>
                                  <span className="font-medium">Last Scanned:</span>
                                  <p className="mt-1 flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {formatDate(source.last_scanned)}
                                  </p>
                                </div>
                              )}
                              
                              {source.suggested_customer_name && (
                                <div>
                                  <span className="font-medium">Suggested Customer:</span>
                                  <p className="mt-1">{source.suggested_customer_name}</p>
                                </div>
                              )}
                            </div>
                            
                            {/* Improved metadata display */}
                            {metadataInfo && (
                              <div className="mt-3 space-y-2">
                                {metadataInfo.channelName && (
                                  <div className="flex items-center space-x-2">
                                    <MessageSquare className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">
                                      Channel: <span className="font-medium">{metadataInfo.channelName}</span>
                                    </span>
                                  </div>
                                )}
                                
                                {metadataInfo.memberCount !== undefined && (
                                  <div className="flex items-center space-x-2">
                                    <Users className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">
                                      Members: <span className="font-medium">{metadataInfo.memberCount}</span>
                                    </span>
                                  </div>
                                )}
                                
                                {metadataInfo.hasExternalUsers !== undefined && (
                                  <div className="flex items-center space-x-2">
                                    {metadataInfo.hasExternalUsers ? (
                                      <CheckCircle className="w-3 h-3 text-green-500" />
                                    ) : (
                                      <X className="w-3 h-3 text-muted-foreground" />
                                    )}
                                    <span className="text-sm text-muted-foreground">
                                      External Users: <span className="font-medium">
                                        {metadataInfo.hasExternalUsers ? 'Yes' : 'No'}
                                      </span>
                                    </span>
                                  </div>
                                )}
                                
                                {metadataInfo.isCustomerChannel !== undefined && (
                                  <div className="flex items-center space-x-2">
                                    {metadataInfo.isCustomerChannel ? (
                                      <CheckCircle className="w-3 h-3 text-blue-500" />
                                    ) : (
                                      <X className="w-3 h-3 text-muted-foreground" />
                                    )}
                                    <span className="text-sm text-muted-foreground">
                                      Customer Channel: <span className="font-medium">
                                        {metadataInfo.isCustomerChannel ? 'Yes' : 'No'}
                                      </span>
                                    </span>
                                  </div>
                                )}
                                
                                {metadataInfo.reasoning && metadataInfo.reasoning.length > 0 && (
                                  <div className="mt-2">
                                    <span className="text-sm font-medium text-muted-foreground">AI Reasoning:</span>
                                    <ul className="mt-1 space-y-1">
                                      {metadataInfo.reasoning.map((reason: string, idx: number) => (
                                        <li key={idx} className="text-xs text-muted-foreground flex items-start">
                                          <span className="text-muted-foreground mr-1">•</span>
                                          {reason}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Remove button */}
                        <div className="ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveSource(source)}
                            disabled={removingSource === source.source_id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            {removingSource === source.source_id ? (
                              <div className="flex items-center space-x-1">
                                <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                                <span>Removing...</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1">
                                <X className="w-3 h-3" />
                                <span>Remove</span>
                              </div>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex justify-end pt-4 border-t border-border">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomersPage;